import React, { useEffect, useState, useContext } from "react";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { MSALContext } from "../MSALContext";
import { getAuthHeaders } from "../Auth/authHelpers";
import axios from "axios";
import { Modal, Button } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import TimePicker from "react-time-picker";
import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";

import "./TechPlan.css";

const TechPlan = () => {
	const { msalInstance } = useContext(MSALContext);
	const [trainerID, setTrainerID] = useState(null);
	const [sites, setSites] = useState([]);
	const [selectedSite, setSelectedSite] = useState("");
	const [technicians, setTechnicians] = useState([]);
	const [techPlans, setTechPlans] = useState([]);
	const [newPlan, setNewPlan] = useState(null);
	const [selectedTech, setSelectedTech] = useState("");
	const [competencyData, setCompetencyData] = useState({});
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [showModal, setShowModal] = useState(false);
	const [replacingCourse, setReplacingCourse] = useState(null);
	const [replacementOptions, setReplacementOptions] = useState({
		fundamentals: [],
		electives: [],
	});
	const [nextCompetency, setNextCompetency] = useState("");
	const [showNextCompetencyModal, setShowNextCompetencyModal] = useState(false);
	const [selectedCompetency, setSelectedCompetency] = useState("");
	const [selectedNextCompetency, setSelectedNextCompetency] = useState("");

	const processedCompetencies = new Set();
	const [competencies, setCompetencies] = useState({
		fundamentals: [],
		electives: [],
	});

	const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

	// 🔹 Fetch Trainer Info (MSAL)
	useEffect(() => {
		if (!msalInstance) {
			setError("MSAL instance is not available.");
			return;
		}

		const accounts = msalInstance.getAllAccounts();
		if (accounts.length > 0) {
			setTrainerID(accounts[0].username);
		} else {
			setError("No accounts found in MSAL instance.");
		}
	}, [msalInstance]);

	// Load parent-child map from backend (derived from the CC matrix).
	// Falls back to the bundled JSON on failure.
	useEffect(() => {
		const shapeData = (data) => {
			const formattedData = {};
			data.forEach((category) => {
				category.stages.forEach((stage, index) => {
					formattedData[stage] = {
						current: stage,
						next: category.stages[index + 1] || null,
					};
				});
			});
			setCompetencyData(formattedData);
		};
		(async () => {
			try {
				const res = await fetch(`${API_URL}/etp-mstr/competency-map`, {
					headers: await getAuthHeaders(trainerID || ""),
				});
				if (!res.ok) throw new Error(`HTTP ${res.status}`);
				const data = await res.json();
				shapeData(Array.isArray(data) ? data : []);
			} catch (err) {
				console.error("Failed to load competency map from API, falling back:", err);
				try {
					const res = await fetch("./competency.json");
					const data = await res.json();
					shapeData(Array.isArray(data) ? data : []);
				} catch (fallbackErr) {
					console.error("Failed to load fallback competency.json:", fallbackErr);
				}
			}
		})();
	}, [trainerID, API_URL]);

	// 🔹 Fetch Technicians when Site is

	useEffect(() => {
		if (selectedSite) {
			fetchTechnicians(selectedSite);
		}
	}, [selectedSite]);

	// 🔹 Fetch Sites Assigned to Trainer
	useEffect(() => {
		if (!trainerID) return;
		setLoading(true);

		axios
			.post(`${API_URL}/dev-plan/get-sites`, { trainerID })
			.then((res) => setSites(["Select a Site", ...res.data.sites]))
			.catch(() => setError("Failed to fetch sites."))
			.finally(() => setLoading(false));
	}, [trainerID]);

	// 🔹 Fetch Competencies when Site & Technician are Selected
	useEffect(() => {
		if (selectedSite && selectedTech) {
			fetchCompetencies(selectedSite, selectedTech);
		}
	}, [selectedSite, selectedTech]);

	// 🔹 Fetch Competencies for a specific site and technician
	const fetchCompetencies = async (site, technician) => {
		if (!site || !technician) {
			setCompetencies({ fundamentals: [], electives: [] });
			return;
		}

		setLoading(true);
		try {
			console.log("📤 Fetching uncompleted competencies for:", {
				site_id: site,
				technicianName: technician,
			});

			const competencyRes = await axios.post(
				`${API_URL}/dev-plan/get-all-competencies`,
				{ site_id: site, technicianName: technician }
			);

			console.log("✅ Uncompleted Competency Data:", competencyRes.data);

			setCompetencies({
				fundamentals: competencyRes.data.fundamentals,
				electives: competencyRes.data.electives,
			});
		} catch (error) {
			console.error("❌ Error fetching uncompleted competencies:", error);
			setError("Failed to fetch competencies.");
		} finally {
			setLoading(false);
		}
	};

	const handleShowModal = async (
		competencyCode,
		techName,
		siteID,
		category
	) => {
		if (!competencyCode || !techName || !siteID || !category) {
			alert(
				"Error: Missing required values. Please ensure a technician and site are selected."
			);
			return;
		}

		console.log("📌 Competency Code:", competencyCode);
		console.log("📌 Technician:", techName);
		console.log("📌 Site ID:", siteID);
		console.log("📌 Category (Fundamental/Elective):", category);

		const next = competencyData[competencyCode]?.next || null;

		if (!next) {
			try {
				// 🔹 Fetch only available competencies
				const competencyRes = await axios.post(
					`${API_URL}/dev-plan/get-available-competencies`,
					{ site_id: siteID, technicianName: techName }
				);

				console.log("✅ Available Competencies Fetched:", competencyRes.data);

				// ✅ Separate based on category (Fundamental vs. Elective)
				let availableCompetencies = [];
				if (category === "Fundamental") {
					availableCompetencies = competencyRes.data.fundamentals.filter(
						(f) => f.completionStatus !== "Completed"
					);
				} else if (category === "Elective") {
					availableCompetencies = competencyRes.data.electives.filter(
						(e) => e.completionStatus !== "Completed"
					);
				}

				console.log(`✅ Available ${category}s:`, availableCompetencies);

				// ✅ Update only the correct category options
				setReplacementOptions((prev) => ({
					...prev,
					[category.toLowerCase() + "s"]: availableCompetencies, // 'fundamentals' or 'electives'
				}));

				// ✅ Set state for modal selection
				setReplacingCourse({
					techName,
					siteID,
					currentCompetency: competencyCode,
					isFundamental: category === "Fundamental",
				});
			} catch (error) {
				console.error("❌ Error fetching replacement competencies:", error);
				alert(
					`Failed to fetch competencies: ${
						error.response?.data?.error || error.message
					}`
				);
			}
		}

		setNextCompetency(next || "No next competency");
		setShowModal(true);
	};

	// 🔹 Handle Replacing a Competency
	const handleReplaceCompetency = async () => {
		if (!replacingCourse || !nextCompetency) {
			console.error("❌ Missing required fields for competency replacement.");
			return;
		}

		console.log("📤 Sending request to replace competency:", {
			technicianName: replacingCourse.techName,
			siteID: replacingCourse.siteID,
			currentCourse: replacingCourse.currentCompetency,
			newCourse: nextCompetency,
		});

		try {
			const response = await axios.post(
				`${API_URL}/dev-plan/replace-competency`,
				{
					technicianName: replacingCourse.techName,
					siteID: replacingCourse.siteID,
					currentCourse: replacingCourse.currentCompetency,
					newCourse: nextCompetency,
				},
				{ headers: { "Content-Type": "application/json" } }
			);

			console.log("✅ Competency Replaced:", response.data);

			// ✅ Refresh state-based data instead of full page reload
			await fetchCompetencies(replacingCourse.siteID, replacingCourse.techName);
			await fetchTechnicians(selectedSite);

			alert("✅ Competency successfully updated!");
			setShowModal(false);
		} catch (error) {
			console.error(
				"❌ Error replacing competency:",
				error.response ? error.response.data : error
			);
			alert("Failed to update competency.");
			setShowModal(false);
		}
	};
	const handleShowNextCompetencyModal = (competencyCode) => {
		if (!competencyCode) {
			alert("No competency selected.");
			return;
		}

		const nextCompetency =
			competencyData[competencyCode]?.next || "No next competency";
		setSelectedCompetency(competencyCode);
		setSelectedNextCompetency(nextCompetency);
		setShowNextCompetencyModal(true);
	};

	const fetchTechnicians = async (site) => {
		if (!site || site === "Select a Site") {
			setTechnicians([]);
			setTechPlans([]);
			return;
		}

		setLoading(true);

		try {
			const res = await axios.post(`${API_URL}/dev-plan/get-technicians`, {
				site,
			});
			const uniqueTechnicians = Array.from(
				new Set(res.data.technicians.map((t) => t.name))
			).map((name) => res.data.technicians.find((t) => t.name === name));

			setTechnicians(uniqueTechnicians);

			const savedPlansRes = await axios.post(
				`${API_URL}/dev-plan/get-tech-plans`,
				{ site }
			);

			// 🔹 Ensure enrollment status is always a string (fix for missing indicators)
			const updatedPlans = savedPlansRes.data.map((plan) => ({
				...plan,
				fund_1_enroll: plan.fund_1_enroll?.toString() ?? "No",
				fund_2_enroll: plan.fund_2_enroll?.toString() ?? "No",
				elect_1_enroll: plan.elect_1_enroll?.toString() ?? "No",
				elect_2_enroll: plan.elect_2_enroll?.toString() ?? "No",
			}));

			setTechPlans(updatedPlans);
		} catch (error) {
			setError("Failed to fetch technicians or saved plans.");
			console.error("❌ Error:", error);
		} finally {
			setLoading(false);
		}
	};

	// 🔹 Move to Next Competency When Completed
	const handleCompletion = async (plan, field) => {
		if (!competencyData[plan[field]]) return;
		const nextCompetency = competencyData[plan[field]].next;

		if (!nextCompetency) {
			alert("No next competency available.");
			return;
		}

		const updatedPlan = { ...plan, [field]: nextCompetency };
		try {
			await axios.post(`${API_URL}/dev-plan/update`, updatedPlan);
			setTechPlans((prevPlans) =>
				prevPlans.map((p) => (p.name === plan.name ? updatedPlan : p))
			);
		} catch (error) {
			console.error("❌ Error updating competency:", error);
		}
	};

	// Function to return color based on enrollment status
	const getStatusColor = (status) => {
		const cleanStatus = status ? status.toString().trim() : "No"; // Ensure valid values

		switch (cleanStatus) {
			case "No":
			case "false":
			case "":
				return "bg-not-enrolled";
			case "Not Started":
				return "bg-primary";
			case "In Progress":
				return "bg-orange";
			case "Completed":
				return "bg-purple";
			default:
				return "bg-secondary";
		}
	};

	const fetchTechnicianAlias = async (wd_name, site) => {
		try {
			const response = await axios.post(
				`${process.env.REACT_APP_API_URL}/dev-plan/get-technicians`,
				{ site }
			);

			const technicians = response.data.technicians;

			// 🔹 Find alias where wd_name matches
			const technician = technicians.find((t) => t.name === wd_name);

			if (!technician) {
				throw new Error(`Alias not found for Workday Name: ${wd_name}`);
			}

			return technician.alias; // ✅ Return etp_mstr_alias
		} catch (error) {
			console.error("❌ Error fetching technician alias:", error);
			return null;
		}
	};

	// Auto-Advance Competencies When a Course is Completed
	useEffect(() => {
		if (!techPlans.length || Object.keys(competencyData).length === 0) return;

		techPlans.forEach((plan) => {
			const fieldsToCheck = ["fund_1", "fund_2", "elect_1", "elect_2"];

			fieldsToCheck.forEach((field) => {
				const competencyCode = plan[field];
				if (
					plan[`${field}_enroll`] === "Completed" &&
					!processedCompetencies.has(competencyCode)
				) {
					const nextCompetency = competencyData[competencyCode]?.next;
					if (nextCompetency) {
						console.log(
							`🔄 Auto-enrolling ${plan.name} into ${nextCompetency}`
						);
						autoAdvanceCompetency(
							plan.name,
							plan.site_id,
							competencyCode,
							nextCompetency
						);
						processedCompetencies.add(competencyCode);
					} else {
						console.log(`⚠️ No next competency found for ${competencyCode}`);
					}
				}
			});
		});
	}, [techPlans, competencyData]);

	// API Call to Move Tech to Next Course
	const autoAdvanceCompetency = async (
		technicianName,
		siteID,
		currentCompetency,
		nextCompetency
	) => {
		try {
			const res = await axios.post(
				`${API_URL}/dev-plan/update-completed-course`,
				{
					technicianName,
					siteID,
					currentCourse: currentCompetency,
					newCourse: nextCompetency,
				}
			);

			if (res.data.message) {
				console.log(
					`✅ Technician ${technicianName} auto-moved to: ${nextCompetency}`
				);

				// ✅ Clear processed competencies to prevent stale state
				processedCompetencies.clear();

				// ✅ Fetch the latest data after update
				await fetchTechnicians(selectedSite);
				await fetchCompetencies(selectedSite, technicianName);

				// ✅ Ensure state update triggers re-render
				setTechPlans((prev) => [...prev]);
			}
		} catch (error) {
			console.error("❌ Error auto-advancing competency:", error);
		}
	};

	// 🔹 Handle Sending Invite (After Fetching Alias)
	const handleSendInviteWithAlias = async (plan) => {
		if (!plan.name || !plan.one_one_day || !plan.one_one_time) {
			alert("⚠️ Please fill in all required fields before sending the email.");
			return;
		}

		try {
			const alias = await fetchTechnicianAlias(plan.name, plan.site_id);
			if (!alias) {
				alert("⚠️ Unable to retrieve technician alias.");
				return;
			}
			handleSendInvite({ ...plan, alias });
		} catch (error) {
			console.error("❌ Error fetching alias:", error);
			alert("Failed to send invite.");
		}
	};

	// 🔹 Handle Sending Email Invite
	const handleSendInvite = async (plan) => {
		if (!plan || !plan.alias || !plan.one_one_day || !plan.one_one_time) {
			alert("Missing required information for sending the email.");
			return;
		}

		const recipientEmail = `${plan.alias}@amazon.com`;
		const subject = `1-on-1 Meeting Invitation from ${trainerID}`;
		const body = `Hello ${plan.alias},\n\nYour manager, ${trainerID}, has scheduled a 1-on-1 meeting with you.\n\n📅 Date: ${plan.one_one_day}\n⏰ Time: ${plan.one_one_time}\n\nPlease confirm your availability.\n\nBest regards,\n${trainerID}`;

		const mailtoLink = `mailto:${recipientEmail}?subject=${encodeURIComponent(
			subject
		)}&body=${encodeURIComponent(body)}`;
		window.open(mailtoLink, "_blank"); // Open email client

		// 🔹 Introduce a delay before prompting confirmation
		setTimeout(() => {
			const emailConfirmed = window.confirm(
				"Did you successfully send the email?"
			);
			if (emailConfirmed) {
				updateEmailStatus(plan);
			}
		}, 3000); // ⏳ Wait 3 seconds before showing the confirmation prompt
	};

	// 🔹 Separate function to update invite status in the database
	const updateEmailStatus = async (plan) => {
		try {
			await axios.post(`${API_URL}/dev-plan/update-email-status`, {
				name: plan.name,
				site_id: plan.site_id,
				invite_sent: 1,
			});

			// ✅ Fetch latest updated plans
			const updatedPlansRes = await axios.post(
				`${API_URL}/dev-plan/get-tech-plans`,
				{ site: selectedSite }
			);

			console.log("📥 Updated tech plans:", updatedPlansRes.data);

			// ✅ Ensure React detects state change
			setTechPlans([]);
			setTimeout(() => setTechPlans([...updatedPlansRes.data]), 50); // Delay ensures re-render

			alert("✅ Email status updated successfully!");
		} catch (error) {
			console.error("❌ Error updating email status:", error);
			alert("Failed to update email status.");
		}
	};

	// 🔹 Handle New Plan Creation
	const addNewPlan = () => {
		setNewPlan({
			name: "",
			ad_account_req: "",
			shift: "",
			manager: trainerID,
			fund_1: "",
			fund_1_enroll: false,
			fund_2: "",
			fund_2_enroll: false,
			elect_1: "",
			elect_1_enroll: false,
			elect_2: "",
			elect_2_enroll: false,
			one_one_day: "",
			one_one_time: "",
			invite_sent: false,
			trn_trainr_enroll: false,
			site_id: selectedSite,
		});
	};

	// 🔹 Handle Inline Edit
	const handleEdit = (field, value) => {
		let formattedValue = value;

		// Ensure only valid fields are processed correctly
		if (field === "one_one_day") {
			// Don't format since it's just a weekday (Monday-Sunday)
			formattedValue = value;
		} else if (field === "one_one_time" && value) {
			// Convert input to 12-hour AM/PM format if it's not already
			const [hours, minutes] = value.split(":");
			let formattedHours = parseInt(hours, 10);
			let ampm = formattedHours >= 12 ? "PM" : "AM";

			formattedHours = formattedHours % 12 || 12; // Convert 0 to 12

			formattedValue = `${formattedHours}:${minutes} ${ampm}`;
		}

		setNewPlan((prev) => ({ ...prev, [field]: formattedValue }));
	};

	// 🔹 Save New Plan
	const handleSave = async () => {
		if (!newPlan || !selectedTech) {
			alert("Please select a technician before saving.");
			return;
		}

		if (!newPlan.shift) {
			alert("Please select a shift.");
			return;
		}

		// Validate Competency Selections
		if (!newPlan.fund_1 || !newPlan.fund_2) {
			alert("Please select both Fundamental competencies.");
			return;
		}
		if (newPlan.fund_1 === newPlan.fund_2) {
			alert("Fundamental selections must be different.");
			return;
		}
		if (!newPlan.elect_1 || !newPlan.elect_2) {
			alert("Please select both Elective competencies.");
			return;
		}
		if (newPlan.elect_1 === newPlan.elect_2) {
			alert("Elective selections must be different.");
			return;
		}

		// Ensure time is formatted in AM/PM before saving
		let formattedTime = newPlan.one_one_time;
		if (newPlan.one_one_time) {
			const timeParts = newPlan.one_one_time.match(/(\d+):(\d+) (\w+)/);
			if (!timeParts) {
				alert("Invalid time format. Please enter time in AM/PM format.");
				return;
			}
			formattedTime = `${timeParts[1]}:${timeParts[2]} ${timeParts[3]}`;
		}

		const formattedPlan = {
			name: selectedTech,
			ad_account_req: newPlan.ad_account_req || "N/A",
			shift: newPlan.shift || "N/A",
			manager: trainerID,
			fund_1: newPlan.fund_1 || "",
			fund_1_enroll: newPlan.fund_1_enroll ? "Not Started" : "No",
			fund_2: newPlan.fund_2 || "",
			fund_2_enroll: newPlan.fund_2_enroll ? "Not Started" : "No",
			elect_1: newPlan.elect_1 || "",
			elect_1_enroll: newPlan.elect_1_enroll ? "Not Started" : "No",
			elect_2: newPlan.elect_2 || "",
			elect_2_enroll: newPlan.elect_2_enroll ? "Not Started" : "No",
			one_one_day: newPlan.one_one_day || "",
			one_one_time: formattedTime, // Ensure AM/PM format is saved
			invite_sent: newPlan.invite_sent ? 1 : 0,
			trn_trainr_enroll: newPlan.trn_trainr_enroll ? 1 : 0,
			site_id: selectedSite,
		};

		try {
			console.log("🚀 Sending Data:", formattedPlan);
			await axios.post(`${API_URL}/dev-plan`, formattedPlan);

			// ✅ Clear processedCompetencies to prevent stale state
			processedCompetencies.clear();

			// ✅ Fetch the updated list of technicians & competencies
			await fetchTechnicians(selectedSite);
			await fetchCompetencies(selectedSite, selectedTech);

			// ✅ Set updated state explicitly to trigger re-render
			setTechPlans((prev) => [...prev]);

			alert("✅ Technician successfully added!");

			// ✅ Reset form without refreshing the page
			setNewPlan(null);
			setSelectedTech("");
		} catch (error) {
			console.error(
				"❌ Error saving tech plan:",
				error.response ? error.response.data : error
			);
			alert("Failed to save tech plan.");
		}
	};

	// 🔹 Filter available technicians (Remove already selected ones)
	const availableTechnicians = technicians.filter(
		(tech) => !techPlans.some((plan) => plan.name === tech.name)
	);

	// 🔹 Prevent selecting the same technician more than once
	useEffect(() => {
		if (selectedTech && techPlans.some((plan) => plan.name === selectedTech)) {
			setSelectedTech(""); // Reset selection if already in use
		}
	}, [selectedTech, techPlans]);

	// 🔹 Handle Delete
	const handleDelete = async (name, site_id) => {
		if (!site_id) {
			alert("Error: Missing site ID for deletion.");
			return;
		}

		if (
			!window.confirm(
				`Are you sure you want to delete ${name}'s plan from ${site_id}?`
			)
		) {
			return;
		}

		try {
			console.log(`🗑️ Deleting tech plan: Name=${name}, Site=${site_id}`);

			await axios.delete(
				`${API_URL}/dev-plan/delete/${encodeURIComponent(
					name
				)}/${encodeURIComponent(site_id)}`
			);

			// ✅ Correctly filter out the deleted item from techPlans
			setTechPlans((prevPlans) =>
				prevPlans.filter(
					(plan) => !(plan.name === name && plan.site_id === site_id)
				)
			);

			console.log(
				`✅ Successfully deleted tech plan for ${name} at site ${site_id}`
			);
		} catch (error) {
			console.error(
				"❌ Error deleting tech plan:",
				error.response ? error.response.data : error
			);
			alert("Failed to delete tech plan.");
		}
	};
	// 🔹 Handle competency selection and update state
	const handleCompetencySelection = (e) => {
		setNextCompetency(e.target.value);
	};

	return (
		<div className="tech-plan-container container">
			<h1 className="text-center">Site Development Plans</h1>
			{error && <p className="text-danger">{error}</p>}
			{loading && <p className="text-info">Loading...</p>}
			{/* 🔹 Select Site Dropdown */}
			<div className="mb-4">
				<label htmlFor="site-select">Select Site:</label>
				<select
					id="site-select"
					className="form-control"
					value={selectedSite}
					onChange={(e) => {
						setSelectedSite(e.target.value);
						fetchTechnicians(e.target.value);
					}}
				>
					{sites.map((site) => (
						<option key={site} value={site}>
							{site}
						</option>
					))}
				</select>
			</div>

			{/* 🔹 Important Note Section */}
			<div
				className="alert alert-warning text-center font-weight-bold p-3 mb-4"
				style={{ border: "2px solid #ff9800", backgroundColor: "#fff3cd" }}
			>
				<h4>🚨 Important Note 🚨</h4>
				<p>
					All <strong>"In Progress"</strong> competencies must be completed{" "}
					<strong>before enrolling</strong> into a new core competency. These
					should be treated as the <strong>top priority</strong> for completion.
				</p>
				<p>
					For a <strong>maximum enrollment</strong> of{" "}
					<strong>2 fundamental</strong> and <strong>2 elective</strong> core
					competencies at any given time.
				</p>
			</div>
			{/* 🔹 Status Key Map */}
			<div className="d-flex justify-content-left gap-3 mb-1 mt-3">
				<span>
					<span className="status-indicator bg-not-enrolled"></span> Not
					Enrolled
				</span>
				<span>
					<span className="status-indicator bg-primary"></span> Enrolled (Not
					Started)
				</span>
				<span>
					<span className="status-indicator bg-orange"></span> In Progress
				</span>
				<span>
					<span className="status-indicator bg-purple"></span> Completed
				</span>
			</div>
			{/* 🔹 Tech Plan Table */}
			<div className="table-responsive">
				<table className="table table-bordered table-hover">
					<thead className="thead-dark">
						<tr>
							<th>Technician Name</th>
							<th>AD Account Request</th>
							<th>Shift</th>
							<th>Manager</th>
							<th>Fund 1</th>
							<th>Enroll Fund 1 Status</th>
							<th>Fund 2</th>
							<th>Enroll Fund 2 Status</th>
							<th>Elective 1</th>
							<th>Enroll Elective 1 Status</th>
							<th>Elective 2</th>
							<th>Enroll Elective 2 Status</th>
							<th>1:1 Scheduled Day</th>
							<th>1:1 Scheduled Time</th>
							<th>Email Sent</th>
							<th>Site</th>
							<th>Actions</th>
						</tr>
					</thead>
					<tbody>
						{techPlans.map((plan, index) => (
							<tr key={plan.name || index}>
								<td>{plan.name}</td>
								<td>{plan.ad_account_req}</td>
								<td>{plan.shift}</td>
								<td>{plan.manager}</td>
								{/* Fund 1 */}
								<td>
									{plan.fund_1}
									{plan.fund_1_enroll === "Completed" && (
										<span className="ml-2">✅</span>
									)}
									<span
										className="ml-2 info-icon"
										onClick={() => handleShowNextCompetencyModal(plan.fund_1)}
										title="View Next Competency"
										style={{ cursor: "pointer", color: "#007bff" }}
									>
										ℹ️
									</span>
								</td>
								<td>
									<span
										className={`status-indicator ${getStatusColor(
											plan.fund_1_enroll
										)}`}
									></span>
									{plan.fund_1_enroll === "Completed" && (
										<button
											className="btn btn-sm btn-primary ml-2"
											onClick={() =>
												handleShowModal(
													plan.fund_1,
													plan.name,
													plan.site_id,
													"Fundamental"
												)
											}
										>
											Choose Next
										</button>
									)}
								</td>

								{/* Fund 2 */}
								<td>
									{plan.fund_2}
									{plan.fund_2_enroll === "Completed" && (
										<span className="ml-2">✅</span>
									)}
									<span
										className="ml-2 info-icon"
										onClick={() => handleShowNextCompetencyModal(plan.fund_2)}
										title="View Next Competency"
										style={{ cursor: "pointer", color: "#007bff" }}
									>
										ℹ️
									</span>
								</td>
								<td>
									<span
										className={`status-indicator ${getStatusColor(
											plan.fund_2_enroll
										)}`}
									></span>
									{plan.fund_2_enroll === "Completed" && (
										<button
											className="btn btn-sm btn-primary ml-2"
											onClick={() =>
												handleShowModal(
													plan.fund_2,
													plan.name,
													plan.site_id,
													"Fundamental"
												)
											}
										>
											Choose Next
										</button>
									)}
								</td>
								{/* Elective 1 */}
								<td>
									{plan.elect_1}
									{plan.elect_1_enroll === "Completed" && (
										<span className="ml-2">✅</span>
									)}
									<span
										className="ml-2 info-icon"
										onClick={() => handleShowNextCompetencyModal(plan.elect_1)}
										title="View Next Competency"
										style={{ cursor: "pointer", color: "#007bff" }}
									>
										ℹ️
									</span>
								</td>
								<td>
									<span
										className={`status-indicator ${getStatusColor(
											plan.elect_1_enroll
										)}`}
									></span>
									{plan.elect_1_enroll === "Completed" && (
										<button
											className="btn btn-sm btn-primary ml-2"
											onClick={() =>
												handleShowModal(
													plan.elect_1,
													plan.name,
													plan.site_id,
													"Elective"
												)
											}
										>
											Choose Next
										</button>
									)}
								</td>

								{/* Elective 2 */}
								<td>
									{plan.elect_2}
									{plan.elect_2_enroll === "Completed" && (
										<span className="ml-2">✅</span>
									)}
									<span
										className="ml-2 info-icon"
										onClick={() => handleShowNextCompetencyModal(plan.elect_2)}
										title="View Next Competency"
										style={{ cursor: "pointer", color: "#007bff" }}
									>
										ℹ️
									</span>
								</td>
								<td>
									<span
										className={`status-indicator ${getStatusColor(
											plan.elect_2_enroll
										)}`}
									></span>
									{plan.elect_2_enroll === "Completed" && (
										<button
											className="btn btn-sm btn-primary ml-2"
											onClick={() =>
												handleShowModal(
													plan.elect_2,
													plan.name,
													plan.site_id,
													"Elective"
												)
											}
										>
											Choose Next
										</button>
									)}
								</td>
								<td>{plan.one_one_day}</td>
								<td>{plan.one_one_time}</td>

								<td>
									{console.log("📌 Invite Sent Status:", plan.invite_sent)}
									{parseInt(plan.invite_sent, 10) === 1 ? (
										<span className="text-success">✅ Sent</span>
									) : (
										<button
											className="btn btn-sm btn-primary"
											onClick={() => handleSendInviteWithAlias(plan)}
										>
											Send Email
										</button>
									)}
								</td>

								<td>{plan.site_id}</td>
								<td>
									<button
										className="btn btn-danger btn-sm"
										onClick={() => handleDelete(plan.name, plan.site_id)}
									>
										Delete
									</button>
								</td>
							</tr>
						))}

						{/* 🔹 New Plan Row */}
						{newPlan && (
							<tr>
								<td>
									<select
										className="form-control"
										value={selectedTech}
										onChange={(e) => setSelectedTech(e.target.value)}
									>
										<option value="">Select Technician</option>
										{availableTechnicians.map((tech) => (
											<option key={tech.name} value={tech.name}>
												{tech.name}
											</option>
										))}
									</select>
								</td>
								<td>
									<input
										type="text"
										className="form-control"
										value={newPlan.ad_account_req}
										onChange={(e) =>
											handleEdit("ad_account_req", e.target.value)
										}
									/>
								</td>
								<td>
									<select
										className="form-control"
										value={newPlan.shift}
										onChange={(e) => handleEdit("shift", e.target.value)}
									>
										<option value="">Select Shift</option>
										<option value="FHD">FHD</option>
										<option value="FHN">FHN</option>
										<option value="BHD">BHD</option>
										<option value="BHN">BHN</option>
									</select>
								</td>
								<td>{trainerID}</td>

								{/* 🔹 Fund 1 Dropdown */}
								<td>
									<select
										className="form-control"
										value={newPlan.fund_1}
										onChange={(e) => handleEdit("fund_1", e.target.value)}
									>
										<option value="">Select Fundamental</option>
										{competencies.fundamentals
											.filter(
												(f) =>
													f.completionStatus &&
													f.completionStatus !== "Completed"
											) // ✅ Exclude completed competencies
											.map((f) => (
												<option
													key={f.uniqueCode}
													value={f.uniqueCode}
													className={getStatusColor(f.completionStatus)}
												>
													{f.label}{" "}
													{f.completionStatus === "In Progress"
														? "(In Progress)"
														: ""}
												</option>
											))}
									</select>
								</td>

								<td>
									<input
										type="checkbox"
										checked={newPlan.fund_1_enroll === true ? true : undefined}
										onChange={(e) =>
											handleEdit("fund_1_enroll", e.target.checked)
										}
										style={{ display: "none" }} // Hides the checkbox
									/>
								</td>
								{/* 🔹 Fund 2 Dropdown (Excludes Fund 1 selection) */}
								<td>
									<select
										className="form-control"
										value={newPlan.fund_2}
										onChange={(e) => handleEdit("fund_2", e.target.value)}
									>
										<option value="">Select Fundamental</option>
										{competencies.fundamentals
											.filter(
												(f) =>
													f.completionStatus !== "Completed" &&
													f.uniqueCode !== newPlan.fund_1
											)
											// ✅ Exclude completed & already selected Fund 1
											.map((f) => (
												<option
													key={f.uniqueCode}
													value={f.uniqueCode}
													className={getStatusColor(f.completionStatus)}
												>
													{f.label}{" "}
													{f.completionStatus === "In Progress"
														? "(In Progress)"
														: ""}
												</option>
											))}
									</select>
								</td>
								<td>
									<input
										type="checkbox"
										checked={newPlan.fund_2_enroll === true ? true : undefined}
										onChange={(e) =>
											handleEdit("fund_2_enroll", e.target.checked)
										}
										style={{ display: "none" }} // Hides the checkbox
									/>
								</td>

								{/* 🔹 Elective 1 Dropdown */}
								<td>
									<select
										className="form-control"
										value={newPlan.elect_1}
										onChange={(e) => handleEdit("elect_1", e.target.value)}
									>
										<option value="">Select Elective</option>
										{competencies.electives
											.filter((e) => e.completionStatus !== "Completed") // ✅ Exclude completed competencies
											.map((e) => (
												<option
													key={e.uniqueCode}
													value={e.uniqueCode}
													className={getStatusColor(e.completionStatus)}
												>
													{e.label}{" "}
													{e.completionStatus === "In Progress"
														? "(In Progress)"
														: ""}
												</option>
											))}
									</select>
								</td>
								<td>
									<input
										type="checkbox"
										checked={newPlan.elect_1_enroll === true ? true : undefined}
										onChange={(e) =>
											handleEdit("elect_1_enroll", e.target.checked)
										}
										style={{ display: "none" }} // Hides the checkbox
									/>
								</td>

								{/* 🔹 Elective 2 Dropdown (Excludes Elective 1 selection) */}
								<td>
									<select
										className="form-control"
										value={newPlan.elect_2}
										onChange={(e) => handleEdit("elect_2", e.target.value)}
									>
										<option value="">Select Elective</option>
										{competencies.electives
											.filter(
												(e) =>
													e.completionStatus !== "Completed" &&
													e.uniqueCode !== newPlan.elect_1
											)
											// ✅ Exclude completed & already selected Elect 1
											.map((e) => (
												<option
													key={e.uniqueCode}
													value={e.uniqueCode}
													className={getStatusColor(e.completionStatus)}
												>
													{e.label}{" "}
													{e.completionStatus === "In Progress"
														? "(In Progress)"
														: ""}
												</option>
											))}
									</select>
								</td>
								<td>
									<input
										type="checkbox"
										checked={newPlan.elect_2_enroll === true ? true : undefined}
										onChange={(e) =>
											handleEdit("elect_2_enroll", e.target.checked)
										}
										style={{ display: "none" }} // Hides the checkbox
									/>
								</td>
								<td>
									<select
										className="form-control"
										value={newPlan.one_one_day}
										onChange={(e) => handleEdit("one_one_day", e.target.value)}
									>
										<option value="">Select a Day</option>
										<option value="Monday">Monday</option>
										<option value="Tuesday">Tuesday</option>
										<option value="Wednesday">Wednesday</option>
										<option value="Thursday">Thursday</option>
										<option value="Friday">Friday</option>
										<option value="Saturday">Saturday</option>
										<option value="Sunday">Sunday</option>
									</select>
								</td>

								<td>
									<TimePicker
										onChange={(value) => handleEdit("one_one_time", value)}
										value={newPlan.one_one_time || ""}
										format="h:mm a"
										disableClock={true}
									/>
								</td>

								<td>{newPlan.site_id}</td>
								<td>
									<button
										className="btn btn-success btn-sm"
										onClick={handleSave}
									>
										Save
									</button>
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
			{/* 🔹 Add Tech Button */}
			<button
				className="btn btn-primary mt-3"
				onClick={addNewPlan}
				disabled={newPlan}
			>
				Add Tech
			</button>
			<Modal show={showModal} onHide={() => setShowModal(false)} centered>
				<Modal.Header closeButton>
					<Modal.Title>
						{nextCompetency !== "No next competency"
							? "Next Competency"
							: "Select a Replacement"}
					</Modal.Title>
				</Modal.Header>

				<Modal.Body>
					{nextCompetency !== "No next competency" ? (
						<p>
							<strong>Next Course:</strong> {nextCompetency}
						</p>
					) : (
						<>
							<p>No next competency available. Please select a new one:</p>

							{replacingCourse?.isFundamental ? (
								<>
									<h5 className="text-primary">Fundamentals</h5>
									<select
										className="form-control"
										value={nextCompetency}
										onChange={handleCompetencySelection}
									>
										<option value="">Select a Fundamental</option>
										{replacementOptions.fundamentals.map((option) => (
											<option key={option.uniqueCode} value={option.uniqueCode}>
												{option.label}
											</option>
										))}
									</select>
								</>
							) : (
								<>
									<h5 className="text-success">Electives</h5>
									<select
										className="form-control"
										value={nextCompetency}
										onChange={handleCompetencySelection}
									>
										<option value="">Select an Elective</option>
										{replacementOptions.electives.map((option) => (
											<option key={option.uniqueCode} value={option.uniqueCode}>
												{option.label}
											</option>
										))}
									</select>
								</>
							)}
						</>
					)}
				</Modal.Body>

				<Modal.Footer>
					{/* ✅ Button is only enabled when a valid competency is selected */}
					<Button
						variant="success"
						onClick={handleReplaceCompetency}
						disabled={
							!nextCompetency || nextCompetency === "No next competency"
						}
					>
						Save New Competency
					</Button>
					<Button variant="secondary" onClick={() => setShowModal(false)}>
						Close
					</Button>
				</Modal.Footer>
			</Modal>
			{/* 🔹 Next Competency Modal */}
			<Modal
				show={showNextCompetencyModal}
				onHide={() => setShowNextCompetencyModal(false)}
				centered
			>
				<Modal.Header closeButton>
					<Modal.Title>Next Core Competency</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p>
						<strong>Current Competency:</strong> {selectedCompetency}
					</p>
					<p>
						<strong>Next Competency:</strong> {selectedNextCompetency}
					</p>
				</Modal.Body>
				<Modal.Footer>
					<Button
						variant="secondary"
						onClick={() => setShowNextCompetencyModal(false)}
					>
						Close
					</Button>
				</Modal.Footer>
			</Modal>
		</div>
	);
};

export default TechPlan;
